import { config } from '@iheartjane/config';
import {
  AddressAutocomplete,
  useMfa,
  validateExpirationDate,
} from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Grid,
  Typography,
  emailRegex,
  telRegex,
  RequiredLabel,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { isValid, parse, subYears } from 'date-fns';
import { useFormContext, useWatch } from 'react-hook-form';

import { useCheckout } from '@jane/shared-ecomm/providers';
import { formatPhone, postMessageToIframeParent } from '@jane/shared/util';

import { StyledGridContainer } from '../checkout.styles';
import { CheckoutAccordionItem } from '../CheckoutAccordion/CheckoutAccordionItem';
import { useCheckoutDocs } from '../util/hooks/useCheckoutDocs';
import { useCheckoutInfo } from '../util/hooks/useCheckoutInfo';
import { useCheckoutInfoIsValid } from '../util/hooks/useCheckoutInfoIsValid';
import { isSameDayOrBefore } from '../util/isSameDayOrBefore';
import { FormImageUpload } from './FormImageUpload';

export const CheckoutInformation = () => {
  const { launch } = useMfa();

  const {
    customer: { authenticated, phone: originalPhone },
    store: {
      birth_date_required,
      checkout_age_minimum,
      customer_pickup_address_required,
    },
    appInfo: { appMode },
    pickupAddressErrorMessage,
    dispatches: { updateCustomerPickupAddress },
  } = useCheckout();

  const { docsValid, formValid } = useCheckoutInfoIsValid();

  const { control } = useFormContext();

  const {
    requiredDocs: {
      governmentId,
      medicalExpirationDate,
      medicalPhoto,
      medicalNumber,
    },
    medicalCardUploadText,
    medicalIdLanguage,
  } = useCheckoutDocs();

  const { handleContinue } = useCheckoutInfo();

  const { firstName, lastName, phone, address, street2 } = useWatch({
    control,
    name: 'customerInformation',
  });
  const isHeadlessMode = appMode === 'headless';

  const fullName = `${firstName} ${lastName}`.trim();
  const showSummary = !!firstName && !!lastName && !!phone;

  const handleSubmitOr2fa = () => {
    if (!authenticated || originalPhone === phone) {
      handleContinue();
      return;
    }
    launch({
      onSuccess: handleContinue,
      newPhone: phone,
    });
  };

  const handlePickupAddressSelection = (address: string) => {
    updateCustomerPickupAddress({ selectedPickupAddress: address });
  };

  const validateAge = (birthDate: string): string | true => {
    if (birthDate?.length < 8) return 'Please fill out this field';

    const minimumAge = checkout_age_minimum || 18;
    const dob = parse(birthDate, 'MMddyyyy', new Date());
    if (!isValid(dob)) return 'Please enter a valid date of birth';

    const minAgeDate = subYears(new Date(), minimumAge);
    const isMinimumAge = isSameDayOrBefore(dob, minAgeDate);

    return isMinimumAge
      ? true
      : `You must be older than ${minimumAge} to continue`;
  };

  const {
    appInfo: { bloomUserExperience },
  } = useCheckout();

  return (
    <CheckoutAccordionItem
      id="info"
      header="Enter your information"
      summary={
        showSummary && (
          <>
            {!!fullName && <Typography>{fullName}</Typography>}
            {!!phone && <Typography>{formatPhone(phone)}</Typography>}
            {!!address && (
              <Typography>
                {address}
                {street2}
              </Typography>
            )}
          </>
        )
      }
    >
      <Box>
        {bloomUserExperience && !authenticated && (
          <Button
            label="Sign in for faster checkout"
            type="button"
            variant="primary"
            onClick={() =>
              postMessageToIframeParent({
                messageType: 'authEvent',
                payload: {
                  name: 'initiateExternalAuth',
                  source: 'checkout',
                },
              })
            }
            full
            mb={24}
          />
        )}
        {governmentId && (
          <Box mb={24}>
            <RequiredLabel>
              <Typography variant="body-semibold" as="span">
                Government ID
              </Typography>
            </RequiredLabel>
            <Typography mb={8}>
              This will help stores validate your identity and will not be made
              visible to anyone else.
            </Typography>
            <FormImageUpload
              name="customerInformation.governmentIdentificationUrl"
              fileName="government_photo"
              label="government ID"
            />
          </Box>
        )}

        {medicalPhoto && (
          <Box mb={24}>
            <RequiredLabel>
              <Typography variant="body-semibold" as="span">
                Medical Marijuana
              </Typography>
            </RequiredLabel>
            <Typography mb={8}>{medicalCardUploadText}</Typography>
            <Flex gap={16}>
              <FormImageUpload
                name="customerInformation.mmjIdFrontUrl"
                fileName="mmj_card"
                label="front of card"
              />
              <FormImageUpload
                name="customerInformation.mmjIdBackUrl"
                fileName="mmj_card_back"
                label="back of card"
              />
            </Flex>
          </Box>
        )}

        <StyledGridContainer spacing={24}>
          {medicalNumber ? (
            <Grid.Item xs={12} lg={6}>
              <Form.TextField
                borderRadius="xs"
                disableMobileInputStyling
                helperText="Include all dashes and special characters"
                label={`ID number on your ${medicalIdLanguage} card`}
                maxLength={100}
                name="customerInformation.mmjIdNumber"
                required
                requiredLabel
                requiredMessage={`Please enter your ${medicalIdLanguage} card ID number.`}
              />
            </Grid.Item>
          ) : (
            <></>
          )}

          {medicalExpirationDate ? (
            <Grid.Item xs={12} lg={6}>
              <Form.NumberField
                allowLeadingZeros
                borderRadius="xs"
                disableMobileInputStyling
                name="customerInformation.mmjExpirationDate"
                label={`${medicalIdLanguage} card expiration date`}
                placeholder="MM/DD/YYYY"
                maskFormat="##/##/####"
                required
                requiredLabel
                requiredMessage={`Please enter your ${medicalIdLanguage} card expiration date.`}
                validate={validateExpirationDate}
              />
            </Grid.Item>
          ) : (
            <></>
          )}

          {customer_pickup_address_required && isHeadlessMode ? (
            <Grid.Item xs={12}>
              <Flex gap={24} flexDirection="row" alignItems="flex-start">
                <Box width="70%">
                  <AddressAutocomplete
                    disableMobileInputStyling
                    googlePlacesKey={config.googlePlacesKey}
                    isForm
                    label="Your address (as required by state law)"
                    name="customerInformation.address"
                    locationIcon
                    placeholder="Enter your address"
                    onSelection={handlePickupAddressSelection}
                  />
                  {pickupAddressErrorMessage && (
                    <Typography mt={4} ml={8} style={{ color: 'red' }}>
                      {pickupAddressErrorMessage}
                    </Typography>
                  )}
                </Box>
                <Box width="30%">
                  <Form.TextField
                    borderRadius="xs"
                    disableMobileInputStyling
                    label="Apt, unit, etc."
                    maxLength={50}
                    name="customerInformation.street2"
                    placeholder="Apt, unit, etc."
                  />
                </Box>
              </Flex>
            </Grid.Item>
          ) : (
            <></>
          )}

          <Grid.Item xs={12}>
            <Typography variant="body-semibold">Contact details</Typography>
            <Typography mb={8}>
              This information will be used to contact you with order updates.
            </Typography>
          </Grid.Item>

          <Grid.Item xs={12} lg={6}>
            <Form.NumberField
              allowLeadingZeros
              borderRadius="xs"
              disableMobileInputStyling
              helperText="Used only for order updates via SMS"
              label="Mobile phone number"
              name="customerInformation.phone"
              maskFormat="(###) ###-####"
              required
              requiredLabel
              requiredMessage="Please enter your phone number."
              validate={(value: string) =>
                !!value.match(telRegex) ||
                'Please enter a valid 10-digit phone number (e.g., (555) 123-4567).'
              }
            />
          </Grid.Item>

          <Grid.Item xs={12} lg={6}>
            <Form.TextField
              borderRadius="xs"
              disableMobileInputStyling
              label="First name on your photo ID"
              maxLength={100}
              name="customerInformation.firstName"
              required
              requiredLabel
              requiredMessage="Please enter the first name on your photo ID."
            />
          </Grid.Item>

          <Grid.Item xs={12} lg={6}>
            <Form.TextField
              borderRadius="xs"
              disableMobileInputStyling
              label="Last name on your photo ID"
              maxLength={100}
              name="customerInformation.lastName"
              required
              requiredLabel
              requiredMessage="Please enter the last name on your photo ID."
            />
          </Grid.Item>

          {birth_date_required ? (
            <Grid.Item xs={12} lg={6}>
              <Form.NumberField
                allowLeadingZeros
                borderRadius="xs"
                disableMobileInputStyling
                name="customerInformation.birthDate"
                label="Birth date"
                placeholder="MM/DD/YYYY"
                maskFormat="##/##/####"
                required
                requiredLabel
                requiredMessage="Please enter your birth date."
                validate={validateAge}
              />
            </Grid.Item>
          ) : (
            <></>
          )}

          <Grid.Item xs={12} lg={6}>
            <Form.TextField
              borderRadius="xs"
              disableMobileInputStyling
              label="Email"
              maxLength={100}
              name="customerInformation.email"
              type="email"
              required
              requiredLabel
              requiredMessage="Please enter your email address."
              validate={(value: string | undefined) =>
                !!value?.match(emailRegex) ||
                'Please enter a valid email address (e.g., name@example.com).'
              }
            />
          </Grid.Item>
        </StyledGridContainer>

        <Button
          onClick={() => handleSubmitOr2fa()}
          disabled={!docsValid || !formValid}
          full
          label="Continue"
          mt={24}
        />
      </Box>
    </CheckoutAccordionItem>
  );
};
